/**
 * Centralized "is the AI stream alive right now?" tracker.
 *
 * Records a timestamp every time a streaming WS frame arrives (text_delta,
 * heartbeat, etc.). Anything that's about to fire a list-messages REST call
 * during a thread can ask `isStreamActive()` and skip if a frame landed
 * within `STREAM_ACTIVITY_WINDOW_MS`.
 *
 * Why this lives here, not in Redux:
 * - The signal is checked from inside an async thunk, where reading
 *   `state.messageSlice.streamingMessageId` would tell us "a stream is in
 *   progress" but NOT "the WS is currently alive". A long-running stream
 *   that has gone quiet (real WS drop) has both `streamingMessageId` set
 *   AND `lastDeltaAt` more than a few seconds old — that's exactly when
 *   we DO want list-messages to fire. So the time-window signal is what
 *   matters, and it's per-tab-not-per-thread.
 * - It's also called from a reducer-adjacent path (the text_delta WS
 *   handler in WebsocketProvider). Keeping it as a module-level mutable
 *   rather than Redux state means we don't trigger a re-render every
 *   time a delta lands at 100/sec — which would be ruinous.
 *
 * Background:
 *   On thread 12a5b88f-… CloudWatch showed three list-messages calls
 *   landing during a 10-second streaming window (frames flowing the
 *   whole time). The recovery-poll watchdog in WebsocketProvider had
 *   correct logic, but its `lastDeltaAtRef` was a closure-local ref
 *   only consulted by THAT one polling site. Other dispatch sites
 *   (~20 of them — see commit aef715a8 for the full inventory) had
 *   no equivalent guard. This module is the shared source of truth so
 *   any new caller automatically gets gated correctly.
 */

const STREAM_ACTIVITY_WINDOW_MS = 5000;

let lastFrameAt = 0;

/**
 * Stamp the activity tracker. Call when any WS frame arrives that proves
 * the stream is alive — text_delta, heartbeat, message_added carrying a
 * streaming text_delta, etc.
 */
export const recordStreamFrame = () => {
  lastFrameAt = Date.now();
};

/**
 * True iff a stream-proving frame arrived within the last `windowMs` ms.
 * Default window matches the recovery-poll watchdog's STREAMING_WS_QUIET_MS.
 */
export const isStreamActive = (windowMs = STREAM_ACTIVITY_WINDOW_MS) => {
  return Date.now() - lastFrameAt < windowMs;
};

/**
 * Telemetry helper — for log lines that want to record "we suppressed a
 * fetch because the stream looked alive". Returns ms-since-last-frame so
 * a caller can log "skipped, last frame 1230ms ago".
 */
export const msSinceLastFrame = () => {
  if (lastFrameAt === 0) return Infinity;
  return Date.now() - lastFrameAt;
};

/**
 * Reset — for tests, and for cases where we want to force the next fetch
 * to go through (e.g., right after explicit thread navigation).
 */
export const resetStreamActivity = () => {
  lastFrameAt = 0;
};

export const STREAM_ACTIVITY_WINDOW_MS_EXPORT = STREAM_ACTIVITY_WINDOW_MS;
